import type {
	ActiveSubscriptionsByPlanType,
	DashboardKPIs,
} from "@pilatestime-admin/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Building2, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { fetchDashboardQueryOptions } from "@/api/dashboard";
import { useApiParameters } from "@/hooks/useApiParameters";

export const Route = createFileRoute("/_auth/app/_app/")({
	loader: async ({ context }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);

		// Preload dashboard data
		context.queryClient.ensureQueryData(fetchDashboardQueryOptions(token));

		return { userId, token };
	},
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation();
	const { token } = Route.useLoaderData();

	const { data: dashboardData } = useSuspenseQuery(
		fetchDashboardQueryOptions(token),
	);

	return (
		<div className="container mx-auto p-6 space-y-6">
			{/* Page Title */}
			<h1 className="text-2xl font-bold text-base-content">
				{t("dashboard.title")}
			</h1>

			{/* KPI Cards */}
			<KPICards kpis={dashboardData.kpis} />

			{/* Charts Row */}
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				<ActiveSubscriptionsChart
					data={dashboardData.activeSubscriptionsByPlanType}
				/>
			</div>
		</div>
	);
}

// ============================================================================
// KPI Cards Component
// ============================================================================

function KPICards({ kpis }: { kpis: DashboardKPIs }) {
	const { t } = useTranslation();

	const cards = [
		{
			label: t("dashboard.kpis.totalOrganizations"),
			value: kpis.totalOrganizations,
			icon: Building2,
			color: "text-primary",
			bgColor: "bg-primary/10",
		},
		{
			label: t("dashboard.kpis.totalUsers"),
			value: kpis.totalUsers,
			icon: Users,
			color: "text-secondary",
			bgColor: "bg-secondary/10",
		},
	];

	return (
		<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
			{cards.map((card) => (
				<div
					key={card.label}
					className="card bg-base-100 border border-base-300 shadow-sm"
				>
					<div className="card-body p-4">
						<div className="flex items-center gap-3">
							<div className={`p-3 rounded-lg ${card.bgColor}`}>
								<card.icon className={`w-6 h-6 ${card.color}`} />
							</div>
							<div>
								<p className="text-sm text-base-content/60">{card.label}</p>
								<p className="text-2xl font-bold font-mono tabular-nums text-base-content">
									{card.value}
								</p>
							</div>
						</div>
					</div>
				</div>
			))}
		</div>
	);
}

// ============================================================================
// Active Subscriptions Chart Component
// ============================================================================

function ActiveSubscriptionsChart({
	data,
}: {
	data: ActiveSubscriptionsByPlanType;
}) {
	const { t } = useTranslation();

	const maxCount = Math.max(
		data.starter,
		data.professional,
		data.enterprise,
		1,
	);

	const plans = [
		{
			key: "starter",
			label: t("dashboard.activeSubscriptions.starter"),
			value: data.starter,
			color: "bg-info",
		},
		{
			key: "professional",
			label: t("dashboard.activeSubscriptions.professional"),
			value: data.professional,
			color: "bg-primary",
		},
		{
			key: "enterprise",
			label: t("dashboard.activeSubscriptions.enterprise"),
			value: data.enterprise,
			color: "bg-success",
		},
	];

	return (
		<div className="card bg-base-100 border border-base-300 shadow-sm">
			<div className="card-body">
				<h2 className="card-title text-lg">
					{t("dashboard.activeSubscriptions.title")}
				</h2>
				<p className="text-sm text-base-content/60">
					{t("dashboard.activeSubscriptions.subtitle")}
				</p>

				<div className="mt-4 space-y-4">
					{plans.map((plan) => (
						<div key={plan.key} className="space-y-1">
							<div className="flex items-center justify-between">
								<span className="text-sm font-medium">{plan.label}</span>
								<span className="text-sm font-bold font-mono tabular-nums">
									{plan.value}
								</span>
							</div>
							<div className="bg-base-200 rounded-full h-4 overflow-hidden">
								<div
									className={`${plan.color} h-full rounded-full transition-all`}
									style={{
										width: `${(plan.value / maxCount) * 100}%`,
									}}
								/>
							</div>
						</div>
					))}
				</div>

				<div className="divider my-2" />

				<div className="flex justify-between text-sm">
					<span className="text-base-content/60">
						{t("dashboard.activeSubscriptions.total")}:
					</span>
					<span className="font-bold">{data.total}</span>
				</div>
			</div>
		</div>
	);
}
